/**
 * A/B Testing System - ADT Landing Page
 * Sistema nativo de A/B testing para optimización de conversiones
 */

import {
    heroMessageConfig,
    formOptimizationConfig,
    generalConfig
} from '../config/abTestConfig';
import trackingManager from './tracking';

class ABTestingManager {
    constructor() {
        this.tests = new Map();
        this.userVariants = new Map();
        this.sessionId = this.generateSessionId();
        this.isDevelopment = generalConfig.debug;
        this.config = generalConfig;
        this.remoteConfig = null;
        this.forceControlFallback = false;

        // Inicializar tests disponibles
        this.initializeTests();

        // Inicializar desde sessionStorage (después de initializeTests)
        this.initializeFromSession();

        // Configurar tracking
        this.setupTracking();

        // Verificar si hay parámetro de reset
        this.checkForReset();

        // Trackear carga del sitio con variantes activas
        this.trackSiteLoad();
    }

    /**
     * Inicializar variantes desde sessionStorage
     */
    initializeFromSession() {
        try {
            // Cargar variantes guardadas en sessionStorage
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('ab_test_')) {
                    const testName = key.replace('ab_test_', '');
                    const variant = sessionStorage.getItem(key);

                    // Solo restaurar si el test existe y la variante es válida
                    const test = this.tests.get(testName);
                    if (variant && test && test.variants.hasOwnProperty(variant)) {
                        this.userVariants.set(testName, variant);
                        if (this.isDevelopment) {
                            console.log(`🧪 Restored from session - ${testName}: ${variant}`);
                        }
                    }
                }
            }
        } catch (error) {
            console.warn('Could not initialize from sessionStorage:', error);
        }
    }

    /**
     * Generar ID único de sesión
     */
    generateSessionId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }

    /**
     * Inicializar configuración de tests
     */
    initializeTests() {
        const heroSrc = this.getNormalizedTestSource('hero_message', heroMessageConfig);
        const formSrc = this.getNormalizedTestSource('form_optimization', formOptimizationConfig);

        // Verificar si el sistema está habilitado
        if (!this.config.enabled) {
            if (this.isDevelopment) {
                console.log('🧪 A/B Testing system is disabled');
            }
            return;
        }

        // Solo inicializar tests activos
        if (this.config.activeTests.includes('form_optimization')) {
            this.tests.set(formSrc.testName, {
                name: formSrc.testName,
                description: formSrc.description,
                variants: formSrc.variants
            });
        }

        if (this.config.activeTests.includes('hero_message')) {
            this.tests.set(heroSrc.testName, {
                name: heroSrc.testName,
                description: heroSrc.description,
                variants: heroSrc.variants
            });
        }

        if (this.isDevelopment) {
            console.log('🧪 Initialized A/B tests:', Array.from(this.tests.keys()));
        }
    }

    /**
     * Normalizar fuentes remotas para que mantengan la misma forma que el fallback local.
     */
    getNormalizedTestSource(testName, fallbackSource) {
        const remoteSource = this.remoteConfig?.[testName];
        if (!remoteSource?.variants) {
            return fallbackSource;
        }

        const normalizedVariants = {};
        const fallbackVariants = fallbackSource?.variants || {};

        const variantNames = new Set([
            ...Object.keys(fallbackVariants),
            ...Object.keys(remoteSource.variants)
        ]);

        variantNames.forEach((variantName) => {
            const variantData = remoteSource.variants?.[variantName] || {};
            const fallbackVariant = fallbackVariants[variantName] || {};
            normalizedVariants[variantName] = {
                name: variantName,
                description: variantData?.description || fallbackVariant.description || variantName,
                weight: typeof variantData?.weight === 'number' ? variantData.weight : (fallbackVariant.weight || 0),
                config: {
                    ...(fallbackVariant.config || {}),
                    ...(variantData?.config || {})
                }
            };
        });

        if (Object.keys(normalizedVariants).length === 0) {
            return fallbackSource;
        }

        return {
            testName: remoteSource.testName || fallbackSource.testName || testName,
            description: remoteSource.description || fallbackSource.description || '',
            variants: normalizedVariants
        };
    }

    /**
     * Aplicar config remota sin esperar a una nueva sesión.
     */
    updateRemoteConfig(remoteConfig) {
        this.remoteConfig = remoteConfig || null;
        this.forceControlFallback = false;
        this.tests.clear();
        this.initializeTests();

        // Limpiar variantes inválidas si la config cambió.
        for (const [testName, variantName] of this.userVariants.entries()) {
            if (!this.isValidVariant(testName, variantName)) {
                this.userVariants.delete(testName);
                try {
                    sessionStorage.removeItem(`ab_test_${testName}`);
                } catch (error) {
                    console.warn(`Could not clear invalid session variant for ${testName}:`, error);
                }
            }
        }

        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('ab-config-updated'));
        }
    }

    /**
     * Si falla la carga remota, usar siempre control como respaldo seguro.
     */
    useControlFallback() {
        this.remoteConfig = null;
        this.forceControlFallback = true;
        this.tests.clear();
        this.userVariants.clear();
        this.initializeTests();

        if (typeof window !== 'undefined') {
            try {
                this.config.activeTests.forEach((testName) => {
                    sessionStorage.removeItem(`ab_test_${testName}`);
                });
            } catch (error) {
                console.warn('Could not clear session variants for control fallback:', error);
            }

            window.dispatchEvent(new CustomEvent('ab-config-updated'));
        }
    }

    /**
     * Guardar variante en sessionStorage
     */
    saveVariantToSession(testName, variant) {
        try {
            const sessionKey = `ab_test_${testName}`;
            sessionStorage.setItem(sessionKey, variant);
            if (this.isDevelopment) {
                console.log(`🧪 Saved to session - ${testName}: ${variant}`);
            }
        } catch (error) {
            console.warn('Could not save to sessionStorage:', error);
        }
    }

    /**
     * Recuperar variante de sessionStorage
     */
    getVariantFromSession(testName) {
        try {
            const sessionKey = `ab_test_${testName}`;
            const variant = sessionStorage.getItem(sessionKey);
            if (variant && this.isValidVariant(testName, variant)) {
                return variant;
            }
        } catch (error) {
            console.warn('Could not read from sessionStorage:', error);
        }
        return null;
    }

    /**
     * Obtener variante para un test específico
     */
    getVariant(testName, forceVariant = null) {
        if (this.isDevelopment) {
            console.log(`🧪 Getting variant for test: ${testName}`);
        }

        if (this.forceControlFallback) {
            this.userVariants.set(testName, 'control');
            return 'control';
        }

        // Mapear nombres de test a parámetros URL
        const paramMap = {
            'form_optimization': 'form',
            'hero_message': 'hero_message'
        };

        // Si ya hay una variante asignada para esta sesión, usarla
        if (this.userVariants.has(testName)) {
            const existingVariant = this.userVariants.get(testName);
            if (this.isDevelopment) {
                console.log(`🧪 Using existing variant for ${testName}: ${existingVariant}`);
            }
            return existingVariant;
        }

        // Forzar variante específica si se proporciona
        if (forceVariant && this.isValidVariant(testName, forceVariant)) {
            this.userVariants.set(testName, forceVariant);
            this.saveVariantToSession(testName, forceVariant);
            if (this.isDevelopment) {
                console.log(`🧪 Force variant for ${testName}: ${forceVariant}`);
            }
            return forceVariant;
        }

        // Verificar parámetros URL
        const urlParams = new URLSearchParams(window.location.search);
        const urlParamName = paramMap[testName] || testName;
        const urlVariant = urlParams.get(urlParamName);

        if (urlVariant && this.isValidVariant(testName, urlVariant)) {
            this.userVariants.set(testName, urlVariant);
            this.saveVariantToSession(testName, urlVariant);
            if (this.isDevelopment) {
                console.log(`🧪 URL Parameter - ${testName}: ${urlVariant}`);
            }
            return urlVariant;
        }

        // Intentar recuperar de sessionStorage
        const sessionVariant = this.getVariantFromSession(testName);
        if (sessionVariant) {
            this.userVariants.set(testName, sessionVariant);
            if (this.isDevelopment) {
                console.log(`🧪 Session variant for ${testName}: ${sessionVariant}`);
            }
            return sessionVariant;
        }

        // Asignar nueva variante
        if (this.isDevelopment) {
            console.log(`🧪 Assigning new random variant for ${testName}`);
        }
        const newVariant = this.assignRandomVariant(testName);
        if (newVariant) {
            this.userVariants.set(testName, newVariant);
            this.saveVariantToSession(testName, newVariant);
            if (this.isDevelopment) {
                console.log(`🧪 New Assignment - ${testName}: ${newVariant}`);
            }
        }

        return newVariant;
    }

    /**
     * Verificar si una variante es válida
     */
    isValidVariant(testName, variant) {
        const test = this.tests.get(testName);
        return test && test.variants.hasOwnProperty(variant);
    }

    /**
     * Asignar variante aleatoria basada en pesos
     */
    assignRandomVariant(testName) {
        const test = this.tests.get(testName);
        if (!test) {
            if (this.isDevelopment) {
                console.warn(`🧪 Test not found: ${testName}`);
            }
            return null;
        }

        const variants = Object.values(test.variants);

        // Verificar que hay variantes válidas
        if (variants.length === 0) {
            if (this.isDevelopment) {
                console.warn(`🧪 No variants found for test: ${testName}`);
            }
            return 'control';
        }

        // Calcular peso total
        const totalWeight = variants.reduce((sum, variant) => sum + variant.weight, 0);

        // Si el peso total es 0, usar control
        if (totalWeight === 0) {
            if (this.isDevelopment) {
                console.warn(`🧪 Total weight is 0 for test ${testName}, using control`);
            }
            return 'control';
        }

        const random = Math.random() * totalWeight;

        if (this.isDevelopment) {
            console.log(`🧪 Weight calculation for ${testName}:`);
            console.log(`   - Variants:`, variants.map(v => `${v.name}:${v.weight}`));
            console.log(`   - Total weight: ${totalWeight}`);
            console.log(`   - Random value: ${random.toFixed(3)}`);
        }

        let currentWeight = 0;
        for (const variant of variants) {
            currentWeight += variant.weight;
            if (this.isDevelopment) {
                console.log(`   - Testing ${variant.name}: ${currentWeight} >= ${random.toFixed(3)} = ${random <= currentWeight}`);
            }
            if (random <= currentWeight) {
                if (this.isDevelopment) {
                    console.log(`🧪 Selected variant for ${testName}: ${variant.name}`);
                }
                return variant.name;
            }
        }

        // Fallback to control
        if (this.isDevelopment) {
            console.log(`🧪 Fallback to control for ${testName}`);
        }
        return 'control';
    }

    /**
     * Obtener configuración de variante
     */
    getVariantConfig(testName, variant = null, location = null) {
        const test = this.tests.get(testName);
        if (!test) return {};

        const variantName = variant || this.getVariant(testName);
        if (!variantName || !test.variants[variantName]) return {};

        const cfg = test.variants[variantName].config;

        // If title is a function, try to resolve it with provided location or session cache
        try {
            const maybeTitle = cfg && cfg.title;
            if (typeof maybeTitle === 'function') {
                let loc = location;
                if (!loc && typeof window !== 'undefined') {
                    try {
                        const raw = sessionStorage.getItem('ipLocation');
                        loc = raw ? JSON.parse(raw) : null;
                    } catch (e) {
                        loc = null;
                    }
                }
                // call title function with location (may be null)
                const resolvedTitle = maybeTitle(loc);
                // return a shallow copy of config with resolved title
                return { ...cfg, title: resolvedTitle };
            }
        } catch (e) {
            if (this.isDevelopment) console.warn('Error resolving dynamic title for', testName, e);
        }

        return cfg;
    }

    /**
     * Verificar parámetro de reset en URL
     */
    checkForReset() {
        if (typeof window === 'undefined') return;

        const urlParams = new URLSearchParams(window.location.search);
        const shouldReset = urlParams.get('reset');

        if (shouldReset === '1' || shouldReset === 'true') {
            this.resetAllTests();

            if (this.isDevelopment) {
                console.log('🧪 A/B Testing system reset from URL parameter');
            }

            // Opcional: Remover el parámetro de la URL después del reset
            const newUrl = new URL(window.location);
            newUrl.searchParams.delete('reset');
            window.history.replaceState({}, document.title, newUrl.toString());
        }
    }

    /**
     * Resetear un test específico
     */
    resetTest(testName) {
        if (typeof window === 'undefined') return;

        try {
            // Remover de memoria
            this.userVariants.delete(testName);

            // Remover de sessionStorage
            const sessionKey = `ab_test_${testName}`;
            sessionStorage.removeItem(sessionKey);
            sessionStorage.removeItem(`${sessionKey}_loaded`);

            if (this.isDevelopment) {
                console.log(`🧪 Reset test: ${testName}`);
            }
        } catch (error) {
            console.warn(`Error resetting test ${testName}:`, error);
        }
    }

    /**
     * Resetear todos los tests
     */
    resetAllTests() {
        if (typeof window === 'undefined') return;

        try {
            // Limpiar memoria
            this.userVariants.clear();

            // Limpiar sessionStorage
            const keysToRemove = [];
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith('ab_test_')) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => sessionStorage.removeItem(key));

            // Generar nuevo session ID
            this.sessionId = this.generateSessionId();

            if (this.isDevelopment) {
                console.log('🧪 All A/B tests reset. New session ID:', this.sessionId);
            }
        } catch (error) {
            console.warn('Error resetting all tests:', error);
        }
    }

    /**
     * Limpiar caché y forzar reasignación
     */
    clearCacheAndReassign() {
        this.resetAllTests();

        // Forzar reasignación de variantes para tests activos
        for (const [testName] of this.tests) {
            const newVariant = this.getVariant(testName);
            if (this.isDevelopment) {
                console.log(`🧪 Reassigned ${testName}: ${newVariant}`);
            }
        }
    }

    /**
     * Configurar sistema de tracking
     */
    setupTracking() {
        // Asegurar que existe dataLayer
        window.dataLayer = window.dataLayer || [];
    }

    /**
     * Trackear exposición a variante
     */
    trackVariantExposure(testName, variant, assignmentMethod = 'random') {
        const test = this.tests.get(testName);
        if (!test) return;

        // Usar el nuevo sistema de tracking
        trackingManager.trackVariantExposure(testName, variant, {
            assignment_method: assignmentMethod,
            session_id: this.sessionId,
            variant_description: test.variants[variant]?.description || variant
        });

        if (this.isDevelopment) {
            console.log(`🧪 Variant exposure tracked - ${testName}: ${variant}`);
        }
    }

    /**
     * Trackear evento de A/B test
     */
    trackEvent(eventName, testName, additionalData = {}) {
        const variant = this.userVariants.get(testName);
        if (!variant) return;

        const eventData = {
            event_name: eventName,
            test_name: testName,
            variant: variant,
            session_id: this.sessionId,
            timestamp: new Date().toISOString(),
            ...additionalData
        };

        // Debug en desarrollo
        if (this.isDevelopment) {
            console.log(`🧪 A/B Test Event - ${eventName}:`, eventData);
        }

        // DataLayer para GTM
        window.dataLayer.push({
            event: 'ab_test_event',
            ab_test_event_name: eventName,
            ab_test_name: testName,
            ab_test_variant: variant,
            ab_test_session_id: this.sessionId,
            ab_test_additional_data: additionalData
        });
    }

    /**
     * Trackear conversión
     */
    trackConversion(testName, additionalData = {}) {
        this.trackEvent('conversion', testName, additionalData);
    }

    /**
     * Trackear paso completado
     */
    trackStepComplete(testName, step, additionalData = {}) {
        this.trackEvent('step_complete', testName, { step, ...additionalData });
    }

    /**
     * Trackear abandono
     */
    trackAbandon(testName, step, additionalData = {}) {
        this.trackEvent('abandon', testName, { step, ...additionalData });
    }

    /**
     * Obtener estado actual de todos los tests
     */
    getTestStatus() {
        const status = {};
        for (const [testName] of this.tests) {
            status[testName] = {
                variant: this.userVariants.get(testName) || 'not_assigned',
                config: this.getVariantConfig(testName)
            };
        }
        return status;
    }

    /**
     * Trackear carga del sitio con variantes activas
     */
    trackSiteLoad() {
        if (typeof window === 'undefined') return;

        const activeVariants = this.getActiveVariants();
        trackingManager.trackSiteLoad(activeVariants);

        if (this.isDevelopment) {
            console.log('🧪 Site loaded with variants:', activeVariants);
        }
    }

    /**
     * Trackear apertura del cotizador
     */
    trackCotizadorOpen() {
        if (typeof window === 'undefined') return;

        const activeVariants = this.getActiveVariants();
        trackingManager.trackCotizadorOpen(activeVariants);

        if (this.isDevelopment) {
            console.log('🧪 Cotizador opened with variants:', activeVariants);
        }
    }


    /**
     * Obtener datos de test para webhook
     */
    getTestDataForWebhook(testName) {
        const variant = this.userVariants.get(testName);
        if (!variant) return {};

        return {
            ab_test_name: testName,
            ab_test_variant: variant,
            ab_test_session_id: this.sessionId
        };
    }

    /**
     * Obtener todas las variantes activas actuales
     */
    getActiveVariants() {
        const activeVariants = {};

        for (const [testName] of this.tests) {
            const variant = this.userVariants.get(testName);
            if (variant) {
                activeVariants[testName] = variant;
            }
        }

        return activeVariants;
    }

    /**
     * Obtener estado detallado de todos los tests (para debugging)
     */
    getTestStatus() {
        const status = {};

        for (const [testName, test] of this.tests) {
            const currentVariant = this.userVariants.get(testName);
            status[testName] = {
                variant: currentVariant || 'no-assigned',
                description: test.description,
                variants: Object.keys(test.variants),
                weights: Object.values(test.variants).map(v => ({ name: v.name, weight: v.weight })),
                totalWeight: Object.values(test.variants).reduce((sum, v) => sum + v.weight, 0),
                config: test.variants[currentVariant]?.config || null
            };
        }

        return status;
    }

    /**
     * Debug: Forzar reasignación de variante con logging detallado
     */
    debugReassignVariant(testName) {
        if (!this.isDevelopment) return;

        console.log(`🧪 ===== DEBUG REASSIGN: ${testName} =====`);

        const test = this.tests.get(testName);
        if (!test) {
            console.log(`❌ Test not found: ${testName}`);
            return;
        }

        console.log(`📊 Test config:`, test);
        console.log(`⚖️ Weights:`, Object.values(test.variants).map(v => `${v.name}:${v.weight}`));

        // Limpiar variante actual
        this.resetTest(testName);

        // Asignar nueva variante
        const newVariant = this.getVariant(testName);
        console.log(`🎯 Final result: ${newVariant}`);
        console.log(`🧪 ===== END DEBUG =====`);

        return newVariant;
    }

    /**
     * Refrescar variantes desde parámetros URL
     */
    refreshFromURL() {
        const urlParams = new URLSearchParams(window.location.search);
        const paramMap = {
            'control': 'form',
            'hero_message': 'hero_message'
        };

        let hasChanges = false;

        for (const [testName] of this.tests) {
            const urlParamName = paramMap[testName] || testName;
            const urlVariant = urlParams.get(urlParamName);
            const currentVariant = this.userVariants.get(testName);

            if (urlVariant && this.isValidVariant(testName, urlVariant)) {
                if (currentVariant !== urlVariant) {
                    this.userVariants.set(testName, urlVariant);
                    this.saveVariantToSession(testName, urlVariant);
                    hasChanges = true;
                    if (this.isDevelopment) {
                        console.log(`🧪 URL Updated - ${testName}: ${currentVariant} → ${urlVariant}`);
                    }
                }
            }
        }

        if (hasChanges && this.isDevelopment) {
            console.log(`🧪 Variants refreshed from URL`);
        }
    }
}

// Crear instancia global
const abTestingManager = new ABTestingManager();

// Exponer métodos de reset globalmente para debugging (solo en desarrollo)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    window.abTestingDebug = {
        reset: () => abTestingManager.resetAllTests(),
        resetTest: (testName) => abTestingManager.resetTest(testName),
        clearAndReassign: () => abTestingManager.clearCacheAndReassign(),
        getStatus: () => abTestingManager.getTestStatus(),
        getVariants: () => abTestingManager.getActiveVariants(),
        sessionId: () => abTestingManager.sessionId,
        debugReassign: (testName) => abTestingManager.debugReassignVariant(testName),
        showWeights: () => {
            console.log('🧪 ===== CURRENT WEIGHTS =====');
            const status = abTestingManager.getTestStatus();
            for (const [testName, data] of Object.entries(status)) {
                console.log(`📊 ${testName}:`);
                data.weights.forEach(w => console.log(`   ${w.name}: ${w.weight}%`));
                console.log(`   Total: ${data.totalWeight}%`);
                console.log(`   Current: ${data.variant}`);
            }
        }
    };
}

export default abTestingManager;
